import React, { useState } from "react";
import { Edit, Trash2, Reply, FileText, Clock } from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "../../../../../lib/utils";
import {
  card,
  surfaceMuted,
  bodySecondary,
  motionStaggerChild,
} from "../../../../../lib/design-system";
import DropdownMenu from "./DropdownMenu";
import CommentForm from "./CommentForm";

const Comment = React.memo(({ comment, level = 0, onReply, onDelete, loading }) => {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const maxLevel = 4;

  const handleReply = async (text, attachments) => {
    await onReply(comment._id, text, attachments);
    setShowReplyForm(false);
  };

  const menuItems = [
    {
      label: "Delete",
      icon: <Trash2 className="w-3.5 h-3.5" />,
      danger: true,
      onClick: () => onDelete(comment._id),
    },
  ];

  return (
    <div className={cn(level > 0 && "ml-4 sm:ml-6 pl-3 sm:pl-4 border-l-2 border-accent1/30")}>
      <motion.div
        {...motionStaggerChild(0)}
        className={cn(card, "p-3 sm:p-4 mb-3 hover:bg-accent1/5 transition-colors")}
      >
        <div className="flex items-start gap-2 sm:gap-3 mb-2">
          <div className="w-8 h-8 rounded-sm bg-accent1/10 text-accent1 flex items-center justify-center text-xs font-bold flex-shrink-0 uppercase">
            {comment.author.name.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm text-gray-900 dark:text-white">
              {comment.author.name}
            </div>
            <div className={cn(bodySecondary, "flex items-center gap-1")}>
              <Clock className="w-3 h-3" />
              {new Date(comment.createdAt).toLocaleDateString()}
            </div>
          </div>
          {!comment.isDeleted && (
            <DropdownMenu items={menuItems} />
          )}
        </div>

        <div className="text-sm text-gray-700 dark:text-gray-300 break-words">
          {comment.isDeleted ? (
            <span className="italic text-gray-400 dark:text-gray-500">
              This comment has been deleted
            </span>
          ) : (
            comment.content
          )}
        </div>

        {comment.attachments &&
          comment.attachments.length > 0 &&
          !comment.isDeleted && (
            <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 space-y-1">
              {comment.attachments.map((file, index) => (
                <div
                  key={index}
                  className={cn(
                    "flex items-center p-2 rounded-sm",
                    surfaceMuted
                  )}
                >
                  <FileText className="w-4 h-4 text-accent1 mr-2 flex-shrink-0" />
                  <span className="text-xs sm:text-sm text-gray-900 dark:text-gray-100 truncate">
                    {typeof file === "string" ? file : file.name || file.fileName}
                  </span>
                </div>
              ))}
            </div>
          )}

        {!comment.isDeleted && level < maxLevel && (
          <div className="mt-2">
            <button
              className="flex items-center gap-1 text-xs text-accent1 hover:text-accent1/80 font-medium transition-colors"
              onClick={() => setShowReplyForm(!showReplyForm)}
            >
              <Reply className="w-3.5 h-3.5" />
              Reply
            </button>
          </div>
        )}
      </motion.div>

      {showReplyForm && (
        <div className="mb-3">
          <div className={cn(surfaceMuted, "p-3 rounded-sm border border-gray-200 dark:border-gray-700")}>
            <CommentForm
              placeholder={`Reply to ${comment.author.name}...`}
              onSubmit={handleReply}
              loading={loading}
              buttonLabel="Reply"
              onCancel={() => setShowReplyForm(false)}
            />
          </div>
        </div>
      )}

      {comment.replies && comment.replies.length > 0 && (
        <div className="mt-1">
          {comment.replies.map((reply) => (
            <Comment
              key={reply._id}
              comment={reply}
              level={level + 1}
              onReply={onReply}
              onDelete={onDelete}
              loading={loading}
            />
          ))}
        </div>
      )}
    </div>
  );
});

Comment.displayName = "Comment";

export default Comment;
